"use client"

import { ArrowLeft, ArrowRight } from "lucide-react"
import { indexVar } from "@/components/marketing/landing-v2/cssVars"
import { PanelBar } from "@/components/marketing/landing-v2/PanelBar"
import { useReveal } from "@/components/marketing/landing-v2/useReveal"
import {
  FREE_TEXT_OPTION_IDS,
  type Landing2QuestionStep,
  type Landing2Stage,
} from "@/lib/landing-v2/content"
import { formatCopy, type Landing2Copy } from "@/lib/landing-v2/copy"

type QuestionPanelProps = {
  copy: Landing2Copy
  stage: Landing2Stage
  steps: Landing2QuestionStep[]
  index: number
  answers: Record<string, string>
  freeText: string
  onAnswer: (optionId: string) => void
  onFreeTextChange: (value: string) => void
  onBack: () => void
  onNext: () => void
  onEscape: () => void
}

export function QuestionPanel({
  copy,
  stage,
  steps,
  index,
  answers,
  freeText,
  onAnswer,
  onFreeTextChange,
  onBack,
  onNext,
  onEscape,
}: QuestionPanelProps) {
  const headRef = useReveal<HTMLElement>()
  const ref = useReveal<HTMLDivElement>()
  const step = steps[index]
  if (!step) return null

  const answer = answers[step.key]
  const questionText = copy.questionText[step.copyKey] ?? step.copyKey
  const isLast = index === steps.length - 1
  const wantsFreeText =
    answer !== undefined && (FREE_TEXT_OPTION_IDS as readonly string[]).includes(answer)

  return (
    <section className="l2-section l2-section-tight" id="questions">
      <div className="l2-container">
        <header className="l2-section-head l2-section-head-center" ref={headRef}>
          <span className="l2-label">{copy.questions.label}</span>
          <h2 className="l2-h2">{copy.questions.title}</h2>
          <p className="l2-section-sub">{copy.questions.subtitle}</p>
        </header>

        <div className="l2-panel l2-panel-narrow" ref={ref}>
          <PanelBar
            label={copy.questions.label}
            stage={stage}
            stageNames={copy.discovery.stages}
          />

          <div className="l2-panel-body">
            <div className="l2-progress">
              <div className="l2-progress-rail" aria-hidden="true">
                {steps.map((item, position) => (
                  <i
                    key={item.key}
                    data-state={
                      position < index ? "done" : position === index ? "active" : "todo"
                    }
                  />
                ))}
              </div>
              <span className="l2-meta">
                {formatCopy(copy.questions.progress, {
                  current: index + 1,
                  total: steps.length,
                })}
              </span>
            </div>

            <div className="l2-question" key={step.key}>
              <h3 className="l2-question-title">{questionText}</h3>

              <div className="l2-options" role="radiogroup" aria-label={questionText}>
                {step.optionIds.map((optionId, position) => (
                  <button
                    key={optionId}
                    type="button"
                    role="radio"
                    aria-checked={answer === optionId}
                    className="l2-option"
                    data-selected={answer === optionId || undefined}
                    style={indexVar(position)}
                    onClick={() => onAnswer(optionId)}
                  >
                    <i className="l2-option-mark" aria-hidden="true" />
                    <span>{copy.options[optionId] ?? optionId}</span>
                  </button>
                ))}
              </div>

              {wantsFreeText && (
                <div className="l2-inline-field">
                  <input
                    className="l2-input"
                    value={freeText}
                    onChange={(event) => onFreeTextChange(event.target.value)}
                    placeholder={copy.questions.freeTextPlaceholder}
                    aria-label={copy.questions.freeTextPlaceholder}
                  />
                </div>
              )}
            </div>

            <div className="l2-panel-foot">
              {index > 0 && (
                <button type="button" className="l2-button l2-button-ghost" onClick={onBack}>
                  <ArrowLeft size={15} strokeWidth={2.2} />
                  {copy.questions.back}
                </button>
              )}
              <button
                type="button"
                className="l2-button"
                disabled={!answer}
                onClick={onNext}
              >
                {isLast ? copy.questions.finish : copy.questions.next}
                <ArrowRight size={15} strokeWidth={2.2} />
              </button>
              <button type="button" className="l2-text-button" onClick={onEscape}>
                {copy.questions.escape}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
